import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Upload, FileText, Mic, ArrowLeft } from 'lucide-react'
import { DocumentUpload } from './DocumentUpload'
import { TextInput } from './TextInput'
import { VoiceInput } from './VoiceInput'

type InputMode = 'selection' | 'files' | 'text' | 'voice'

export function KnowledgeInput() {
  const [mode, setMode] = useState<InputMode>('selection')

  const options = [
    {
      id: 'files',
      title: 'Wissen aus Dateien',
      description: 'Dateien werden verarbeitet und in die Wissensdatenbank aufgenommen',
      icon: Upload,
      color: 'from-blue-600 to-blue-700'
    },
    {
      id: 'text',
      title: 'Text eingeben',
      description: 'Direkteingabe von Texten für die Wissensdatenbank',
      icon: FileText,
      color: 'from-green-600 to-green-700'
    },
    {
      id: 'voice',
      title: 'Sprache aufnehmen',
      description: 'Sprachaufnahmen mit Transkription für die Wissensdatenbank',
      icon: Mic,
      color: 'from-purple-600 to-purple-700'
    }
  ]

  if (mode === 'selection') {
    return (
      <div>
        <h2 className="text-2xl font-bold mb-2">Wissen hinzufügen</h2>
        <p className="text-gray-400 mb-8">
          Wählen Sie aus, wie Sie neue Informationen zur Wissensdatenbank hinzufügen möchten.
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          {options.map((option) => {
            const Icon = option.icon
            return (
              <motion.button
                key={option.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setMode(option.id as InputMode)}
                className="glass rounded-xl p-6 text-left hover:bg-white/5 transition-all group"
              >
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${option.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{option.title}</h3>
                <p className="text-sm text-gray-400">{option.description}</p>
              </motion.button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div>
      <button
        onClick={() => setMode('selection')}
        className="mb-6 flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Zurück zur Auswahl
      </button>

      <AnimatePresence mode="wait">
        <motion.div
          key={mode}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
        >
          {mode === 'files' && <DocumentUpload />}
          {mode === 'text' && <TextInput />}
          {mode === 'voice' && <VoiceInput />}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}